import { useEffect, useState } from 'react'
import IntegrationCard from './IntegrationCard'
import { useAppDispatch } from '../../reducers/store'
import { fetchIntegrationsList, selectIntegrations, setIntegrationsToShow } from '../../reducers/integrations'
import { useDispatch, useSelector } from 'react-redux'
import { IntegrationDetails, PageState } from '../../types/IntegrationTypes'
import IntegrationPopout from './IntegrationPopout'
import { Button, IconButton, TextField } from '@radix-ui/themes'
import { CrossCircledIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import toast from 'react-hot-toast'
import { useNavigate, useLocation } from 'react-router-dom'
import IntegrationSkeleton from '../skeleton/IntegrationSkeleton'
import { inPov } from '../../utils/session'
import { selectUser } from '../../reducers/users'
import { HEADER_HEIGHT_WITH_TRIAL, HEADER_HEIGHT_WITHOUT_TRIAL } from '../../strings/consts'
import { BOTTOM_TOAST_CONFIG } from '../toast/consts'

function IntegrationsWrapper() {
  const di = useAppDispatch()
  const { integrationsList, integrationsToShow } = useSelector(selectIntegrations)
  const [showIntegrationPopout, setShowIntegrationPopout] = useState(false)
  const [popoutIntegration, setPopoutIntegration] = useState<IntegrationDetails | undefined>(undefined)
  const [searchFilter, setSearchFilter] = useState<string>('')
  const [activeIntegrations, setActiveIntegrations] = useState<IntegrationDetails[] | undefined>(undefined)
  const [activeScim, setActiveScim] = useState<string | undefined>(undefined)
  const [activeConditionalAccess, setActiveConditionalAccess] = useState<string | undefined>(undefined)
  const [availableIntegrations, setAvailableIntegrations] = useState<IntegrationDetails[] | undefined>(undefined)
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [hasActive, setHasActive] = useState<boolean>(false)
  const categories = ['All', 'SCIM', 'Notifier', 'Conditional Access']
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useSelector(selectUser)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const success = queryParams.get('success')
    if (success) {
      if (success === 'true') {
        toast.success('Integration saved successfully', BOTTOM_TOAST_CONFIG)
      } else {
        toast.error('Failed to save integration', BOTTOM_TOAST_CONFIG)
        dispatch(setIntegrationsToShow('available'))
      }
      queryParams.delete('success')
      queryParams.delete('type')
      queryParams.delete('error')
      navigate({ pathname: location.pathname, search: queryParams.toString() }, { replace: true })
    }
  }, [location, navigate])

  useEffect(() => {
    getIntegrationList()
  }, [])

  useEffect(() => {
    if (integrationsToShow == undefined && activeIntegrations) {
      dispatch(setIntegrationsToShow(activeIntegrations?.length > 0 ? 'active' : 'available'))
    }
  }, [activeIntegrations])

  useEffect(() => {
    if (integrationsToShow === 'active' && integrationsList) {
      const active =
        integrationsList
          .filter((integration) => integration.pageState === PageState.ACTIVE)
          .flatMap((activeIntegration) => activeIntegration.integrations).length > 0
      if (!active) {
        setSelectedCategory('All')
        setSearchFilter('')
      }
      setHasActive(active)
    }
  }, [integrationsToShow, integrationsList])

  useEffect(() => {
    if (popoutIntegration) {
      const active = integrationsList?.find((integration) => integration.pageState === PageState.ACTIVE)
      if (active) {
        const activeIntegration = active.integrations.find(
          (integration) => integration.serverValue === popoutIntegration?.serverValue
        )
        if (activeIntegration) setPopoutIntegration(activeIntegration)
      }
    }
  }, [integrationsList])

  useEffect(() => {
    if (integrationsList) {
      const active = integrationsList
        .filter((integration) => integration.pageState === PageState.ACTIVE)
        .flatMap((activeIntegration) =>
          activeIntegration.integrations.map((integration) => ({
            ...integration,
            isActive: true,
          }))
        )
      setActiveIntegrations(active)

      const scimIntegration = active.find((integration) => integration.category === 'SCIM' && integration.isActive)
      setActiveScim(scimIntegration ? scimIntegration.name : undefined)

      const caIntegration = active.find(
        (integration) => integration.category === 'Conditional Access' && integration.isActive
      )
      setActiveConditionalAccess(caIntegration ? caIntegration.name : undefined)

      const available = integrationsList
        .filter((integration) => integration.pageState === PageState.INACTIVE)
        .flatMap((inactiveIntegration) =>
          inactiveIntegration.integrations.map((integration) => ({
            ...integration,
            isActive: false,
          }))
        )
      setAvailableIntegrations([...available, ...active])
    }
  }, [integrationsList])

  useEffect(() => {
    if (integrationsList) {
      let filteredActiveIntegrations = integrationsList
        .filter((integration) => integration.pageState === PageState.ACTIVE)
        .flatMap((activeIntegration) =>
          activeIntegration.integrations.map((integration) => ({ ...integration, isActive: true }))
        )
      let filteredAvailableIntegrations = integrationsList
        .filter((integration) => integration.pageState === PageState.INACTIVE)
        .flatMap((inactiveIntegration) =>
          inactiveIntegration.integrations.map((integration) => ({ ...integration, isActive: false }))
        )

      if (searchFilter !== '') {
        filteredActiveIntegrations =
          filteredActiveIntegrations?.filter((integration) =>
            integration.name.toLowerCase().includes(searchFilter.toLowerCase())
          ) || []
        filteredAvailableIntegrations =
          filteredAvailableIntegrations?.filter((integration) =>
            integration.name.toLowerCase().includes(searchFilter.toLowerCase())
          ) || []
      }
      if (selectedCategory !== 'All') {
        filteredActiveIntegrations = filteredActiveIntegrations.filter(
          (integration) => integration.category == selectedCategory
        )
        filteredAvailableIntegrations = filteredAvailableIntegrations.filter(
          (integration) => integration.category == selectedCategory
        )
      }

      setActiveIntegrations(filteredActiveIntegrations)
      setAvailableIntegrations([...filteredAvailableIntegrations, ...filteredActiveIntegrations])
    }
  }, [searchFilter, selectedCategory])

  const getIntegrationList = async () => {
    await di(fetchIntegrationsList())
  }

  const manageIntegration = (integration: IntegrationDetails) => {
    setPopoutIntegration(integration)
    setShowIntegrationPopout(true)
  }

  const clearSearchFilter = () => {
    setSearchFilter('')
  }

  const buttonCategory = (category: string) => {
    return (
      <Button
        key={`btn_category_${category}`}
        variant="solid"
        size="3"
        className={`h-9 text-sm cursor-pointer justify-start p-2 ${category == selectedCategory ? 'bg-lightViolet text-menuPurple dark:text-mainText' : 'bg-neutral-2 dark:bg-contentBackground text-mainText'}`}
        onClick={() => {
          setSelectedCategory(category)
        }}
      >
        {category}
      </Button>
    )
  }

  const emptyState = (tab: string) => {
    return (
      <div
        className={`block items-center mt-20 ${hasActive || integrationsToShow !== 'active' ? 'lg:pr-[250px]' : 'lg:pr-[35px]'}`}
      >
        <div className="flex items-center justify-center">
          <MagnifyingGlassIcon className="h-12 w-12" />
        </div>
        {selectedCategory !== 'All' || searchFilter !== '' ? (
          <p className="text-center font-bold">
            No <span className="capitalize">{tab}</span> integrations for filter criteria{' '}
          </p>
        ) : (
          <p className="text-center font-bold capitalize">No {tab} Integrations</p>
        )}
        {selectedCategory !== 'All' || searchFilter !== '' ? (
          <Button
            className="mt-4 text-center flex bg-black mx-auto h-auto min-h-8 cursor-pointer outline-1 dark:outline dark:outline-white"
            onClick={() => {
              setSelectedCategory('All')
              clearSearchFilter()
            }}
          >
            Clear Filters
          </Button>
        ) : (
          <Button
            className="mt-4 text-center flex bg-black border border-neutral-5 text-white dark:bg-transparent mx-auto h-auto min-h-8 cursor-pointer outline-1 dark:outline dark:outline-white"
            onClick={() => {
              dispatch(setIntegrationsToShow(tab === 'active' ? 'available' : 'active'))
            }}
          >
            Switch to {tab === 'active' ? 'Available' : 'Active'} Integrations
          </Button>
        )}
      </div>
    )
  }

  const getIntegrationTooltip = (integration: IntegrationDetails): string | undefined => {
    if (
      integration.category === 'Conditional Access' &&
      activeConditionalAccess &&
      activeConditionalAccess !== integration.name
    ) {
      return `Your organization is currently configured to use ${activeConditionalAccess}. To configure a different Conditional Access integration, first disconnect the active one.`
    }

    if (activeScim && activeScim !== integration.name && integration.category === 'SCIM') {
      return `Your organization is currently configured to use ${activeScim} for SCIM provisioning. To configure a different SCIM integration, first disconnect the active one.`
    }

    return undefined
  }

  const isIntegrationDisabled = (integration: IntegrationDetails): boolean => {
    if (
      integration.category === 'Conditional Access' &&
      activeConditionalAccess &&
      activeConditionalAccess !== integration.name
    ) {
      return true
    }

    if (activeScim && activeScim !== integration.name && integration.category === 'SCIM') {
      return true
    }

    return false
  }

  return (
    <>
      <div
        className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l w-[calc(100%_-_208px)] bg-neutral-2 dark:bg-contentBackground flex gap-4 p-6"
        style={{
          height: inPov(session)
            ? `calc(100% - ${HEADER_HEIGHT_WITH_TRIAL})`
            : `calc(100% - ${HEADER_HEIGHT_WITHOUT_TRIAL})`,
        }}
      >
        {!integrationsList ? (
          <IntegrationSkeleton />
        ) : (
          <>
            {(hasActive || integrationsToShow !== 'active') && (
              <div className="flex flex-col gap-6">
                <TextField.Root
                  value={searchFilter}
                  placeholder="Search"
                  className="w-[200px] h-[38px] bg-inputBackground text-mainText dark:shadow-none border border-transparent dark:border-neutral-11 hover:shadow-none hover:border hover:border-borderPurple hover:dark:border-borderPurple focus:ring-mainBorder focus:ring-borderPurple p-0"
                  onChange={(e) => {
                    setSearchFilter(e.target.value)
                  }}
                >
                  <TextField.Slot className="p-0"></TextField.Slot>
                  {searchFilter !== '' && (
                    <TextField.Slot>
                      <IconButton
                        size="1"
                        variant="ghost"
                        onClick={() => {
                          clearSearchFilter()
                        }}
                      >
                        <CrossCircledIcon height="14" width="14" className="cursor-pointer dark:text-white" />
                      </IconButton>
                    </TextField.Slot>
                  )}
                </TextField.Root>
                <div className="flex flex-col gap-3">
                  <div className="uppercase text-neutral-11 dark:text-neutral-6 text-xs font-medium tracking-widest pl-2">
                    Category
                  </div>
                  <div className="flex flex-col">{categories.map((cat) => buttonCategory(cat))}</div>
                </div>
              </div>
            )}
            <div className="mx-auto w-full pr-8 overflow-y-auto">
              {integrationsList && (
                <div className="mx-auto">
                  {integrationsToShow === 'active' && (
                    <>
                      {activeIntegrations && activeIntegrations?.length > 0 ? (
                        <>
                          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                            {activeIntegrations?.map((integration) => (
                              <IntegrationCard
                                key={integration.serverValue}
                                integration={integration}
                                manageIntegration={manageIntegration}
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        emptyState('active')
                      )}
                    </>
                  )}
                  {integrationsToShow === 'available' && (
                    <>
                      {availableIntegrations && availableIntegrations?.length > 0 ? (
                        <>
                          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                            {availableIntegrations?.map((integration) => (
                              <IntegrationCard
                                key={integration.serverValue}
                                integration={integration}
                                manageIntegration={manageIntegration}
                                tooltip={getIntegrationTooltip(integration)}
                                disabled={isIntegrationDisabled(integration)}
                              />
                            ))}
                          </div>
                        </>
                      ) : (
                        emptyState('available')
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {showIntegrationPopout && (
        <IntegrationPopout
          popoutIntegration={popoutIntegration}
          setShowPopout={setShowIntegrationPopout}
          available={
            activeIntegrations
              ? activeIntegrations.every(
                  (integration: IntegrationDetails) => integration.serverValue !== popoutIntegration?.serverValue
                )
              : false
          }
        />
      )}
    </>
  )
}

export default IntegrationsWrapper
